import React, { useState, useEffect, useContext } from "react";
import { Card, CardHeader, CardBody, Container, Row, Col, Button, Input } from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import { useNavigate, useParams } from "react-router-dom";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import DetailsHeader from "components/CommonComponents/DetailsHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "constants/CommonConstants";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { useDispatch, useSelector } from "react-redux";
import {
  getIssuanceEligibleTrainees,
  generateUserCertificate,
} from "action/issuance";
import { issuanceEligibleTrainees } from "constants/issuance";
import { traineesCertificate } from "constants/certificate";

const IssuanceDetails = () => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;

  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const decodedId = id ? urlDecodedId(id) : null;

  const [eligiblePageNum, setEligiblePageNum] = useState(1);
  const [notEligiblePageNum, setNotEligiblePageNum] = useState(1);

  const [selectedTraineeIds, setSelectedTraineeIds] = useState([]);

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [filterState, setFilterState] = useState({
    searchName: "",
    searchEmail: "",
    searchMobile: "",
    filterParams: [],
  });

  const [isIssuingCertificate, setIsIssuingCertificate] = useState(false);

  const eligibleTraineesResponse = useSelector(
    (state) => state.issuance.eligibleTraineesData,
  );

  useEffect(() => {
    if (decodedId) {
      getIssuanceEligibleTrainees(
        dispatch,
        decodedId,
        eligiblePageNum,
        notEligiblePageNum,
        filterState.filterParams,
      );
    }
    return () => {
      dispatch({ type: issuanceEligibleTrainees, payload: {} });
    };
  }, [decodedId, dispatch, eligiblePageNum, notEligiblePageNum, filterState.filterParams]);
  useEffect(() => {
    if (eligibleTraineesResponse?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          eligibleTraineesResponse?.data?.eligible_trainees?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (
      eligibleTraineesResponse?.code || eligibleTraineesResponse?.message
    ) {
      setListState({
        isLoading: false,
        error: eligibleTraineesResponse?.message || "Failed to load trainees",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [eligibleTraineesResponse]);

  const data = eligibleTraineesResponse?.data || {};
  const batchDetails = {
    title: data.batch_name || "-",
    status: data.batch_status || "-",
    courseName: data.course_name || "-",
    batchStartDate: data.batch_start_date || "-",
    batchEndDate: data.batch_end_date || "-",
    id: decodedId || "0"
  };

  const eligibleTableData = eligibleTraineesResponse?.data?.eligible_trainees?.map(
    (item, index) => {
      return {
        ...item,
        traineeName: item.full_name || item.traineeName || "-",
        mobileNumber: item.mobile || item.mobileNumber || "-",
        email: item.email || "-",
        srNo:
          (eligiblePageNum - 1) *
          (eligibleTraineesResponse?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    },
  );

  const notEligibleTableData = eligibleTraineesResponse?.data?.not_eligible_trainees?.map(
    (item, index) => {
      return {
        ...item,
        traineeName: item.full_name || item.traineeName || "-",
        mobileNumber: item.mobile || item.mobileNumber || "-",
        email: item.email || "-",
        srNo:
          (notEligiblePageNum - 1) *
          (eligibleTraineesResponse?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    },
  );

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName?.trim()) {
      filterParams.push({
        field: "FullName",
        condition: "like",
        filterValues: [filterState.searchName.trim()],
      });
    }

    if (filterState.searchEmail?.trim()) {
      filterParams.push({
        field: "Email",
        condition: "like",
        filterValues: [filterState.searchEmail.trim()],
      });
    }

    if (filterState.searchMobile?.trim()) {
      filterParams.push({
        field: "Mobile",
        condition: "like",
        filterValues: [filterState.searchMobile.trim()],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (eligiblePageNum !== 1) {
        setEligiblePageNum(1);
      }
      if (notEligiblePageNum !== 1) {
        setNotEligiblePageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    setFilterState({
      searchName: "",
      searchEmail: "",
      searchMobile: "",
      filterParams: [],
    });

    if (eligiblePageNum !== 1) {
      setEligiblePageNum(1);
    }
    if (notEligiblePageNum !== 1) {
      setNotEligiblePageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleTraineeCheckboxChange = (traineeID) => {
    setSelectedTraineeIds((prev) => {
      if (prev.includes(traineeID)) {
        return prev.filter((id) => id !== traineeID);
      }

      return [...prev, traineeID];
    });
  };

  const handleIssueCertificate = () => {
    const hasSelectedTrainees = selectedTraineeIds.length > 0;
    const eligibleTraineeCount =
      eligibleTraineesResponse?.data?.totalEligibleTraineesRecords || 0;

    const hasEligibleTrainees = eligibleTraineeCount > 0;

    if (!hasEligibleTrainees && !hasSelectedTrainees) {
      notify(
        "warning",
        "There are no eligible trainees or selected not eligible trainees to issue certificates."
      );
      return;
    }

    showAlert({
      title: "Are you sure you want to issue certificates?",
      message:
        hasEligibleTrainees && hasSelectedTrainees
          ? `Certificates will be issued to all eligible trainees and ${selectedTraineeIds.length} selected not eligible trainee${selectedTraineeIds.length > 1 ? "s" : ""
          }.`
          : hasEligibleTrainees
            ? "Certificates will be issued to all eligible trainees."
            : `Certificates will be issued to ${selectedTraineeIds.length} selected not eligible trainee${selectedTraineeIds.length > 1 ? "s" : ""
            }.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          setIsIssuingCertificate(true);
          dispatch({ type: traineesCertificate, payload: {}, });

          const res = await generateUserCertificate(decodedId, selectedTraineeIds.length > 0
            ? { trainee_ids: selectedTraineeIds }
            : {});
          if (res?.code === 200) {
            const newCertificateData = {
              ...res.data,
              userCertificates: (res.data?.userCertificates || []).map(
                (certificate) => ({
                  ...certificate,
                  certificateGenerated: false,
                })
              ),
            };

            dispatch({ type: traineesCertificate, payload: newCertificateData, });
            navigate(role === "SUPER_ADMIN"
              ? "/super-admin/certificate/issuance/generation"
              : "/admin/certificate/issuance/generation", {
              state: {
                batchDetails: batchDetails,
              },
            });

            getIssuanceEligibleTrainees(
              dispatch,
              decodedId,
              eligiblePageNum,
              notEligiblePageNum,
              filterState.filterParams,
            );

            // const successCount = res?.data?.success_count || 0;
            // const failedCount = res?.data?.failed_count || 0;

            // let message = "";

            // if (successCount > 0 && failedCount > 0) {
            //   message = `${successCount} certificate${successCount > 1 ? "s have" : " has"} been issued successfully. ${failedCount} certificate${failedCount > 1 ? "s could" : " could"} not be issued.`;
            // } else if (successCount > 0) {
            //   message = `${successCount} certificate${successCount > 1 ? "s have" : " has"} been issued successfully.`;
            // } else if (failedCount > 0) {
            //   message = `Failed to issue ${failedCount} certificate${failedCount > 1 ? "s" : ""}. Please try again.`;
            // } else {
            //   message = res?.message || "No certificates were issued.";
            // }

            // showAlert({
            //   title: failedCount > 0 ? "Certificate Issuance Completed" : "Success",
            //   message: message,
            //   type: failedCount > 0 ? "warning" : "success",
            //   confirmStyle: "deep-blue",
            //   confirmText: "OK",
            //   showCancel: false,
            //   onConfirm: (closeAlert) => { closeAlert(); },
            // });

            // getIssuanceEligibleTrainees(
            //   dispatch,
            //   decodedId,
            //   eligiblePageNum,
            //   filterState.filterParams,
            // );

            // setSelectedTraineeIds([]);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
              : res?.message || "Failed to issue certificates!";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong!");
        } finally {
          setIsIssuingCertificate(false);
        }
      },
    });
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 70,
      centerAlign: true,
    },
    {
      Header: "Trainee Name",
      accessor: "traineeName",
      minWidth: 220,
      maxWidth: 300,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Email",
      accessor: "email",
      minWidth: 220,
      maxWidth: 250,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Mobile Number",
      accessor: "mobileNumber",
      minWidth: 150,
      maxWidth: 150,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
  ];

  const notEligibleColumns = [
    {
      Header: "Select",
      accessor: "select",
      minWidth: 50,
      maxWidth: 50,
      centerAlign: true,
      disableSortBy: true,
      Cell: ({ row }) => {
        const traineeID = row.original.trainee_id;

        return (
          <div className="d-flex justify-content-center">
            <Input
              type="checkbox"
              checked={selectedTraineeIds.includes(traineeID)}
              onChange={() => handleTraineeCheckboxChange(traineeID)}
            />
          </div>
        );
      },
    },
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 70,
      centerAlign: true,
    },
    {
      Header: "Trainee Name",
      accessor: "traineeName",
      minWidth: 220,
      maxWidth: 300,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Email",
      accessor: "email",
      minWidth: 220,
      maxWidth: 250,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Mobile Number",
      accessor: "mobileNumber",
      minWidth: 150,
      maxWidth: 150,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
  ];

  return (
    <>
      <AdminHeader />
      {isIssuingCertificate && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-4" />
            <div className="font-weight-bold text-dark">
              Issuing certificates... Please wait.
            </div>
          </div>
        </div>
      )}

      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
             <DetailsHeader isLoading={!eligibleTraineesResponse?.data}
              onBack={() => navigate(-1)}
              backTooltip="Back to Issuance List"
              title={batchDetails.title}
              status={batchDetails.status}
              showEdit={false}
              chips={[
                {
                  id: "courseChip",
                  icon: "fas fa-book",
                  value: batchDetails.courseName,
                  tooltip: `Course: ${batchDetails.courseName}`,
                },
                {
                  id: "dateChip",
                  icon: "fas fa-calendar",
                  value: `${batchDetails.batchStartDate} to ${batchDetails.batchEndDate}`,
                  tooltip: "Batch Dates",
                },
              ]}
            />

            <Card className="section-cards mt-3">
              <CardHeader className="section-header td-card-header">
                <div className="d-flex justify-content-between align-items-center flex-wrap w-100">
                  <h4 className="mb-0 text-default section-title">
                    Eligible Trainees
                    <span className="total-records ml-2">
                      {filterState.filterParams.length > 0
                        ? `(Showing ${eligibleTraineesResponse?.data?.totalFilteredEligibleTraineesRecords || 0} of ${eligibleTraineesResponse?.data?.totalEligibleTraineesRecords || 0} Records)`
                        : `(Total Records: ${eligibleTraineesResponse?.data?.totalEligibleTraineesRecords || 0})`}
                    </span>
                  </h4>

                  {eligibleTraineesResponse?.data?.eligible_trainees?.length >
                    0 && (
                      <Button
                        color="default"
                        onClick={handleIssueCertificate}
                        className="btn-deep-blue"
                      >
                        Issue Certificate
                      </Button>
                    )}
                </div>
                <div className="d-flex align-items-center flex-wrap w-100">
                  {eligibleTraineesResponse?.data?.eligible_trainees?.length >
                    0 && eligibleTraineesResponse?.data
                      ?.totalNotEligibleTraineesRecords > 0 && <div className="text-muted small">
                      <i className="fas fa-info-circle mr-1" />
                      To issue certificates to not eligible trainees, select the required
                      trainees using the checkboxes below.
                    </div>}

                  {eligibleTraineesResponse?.data?.eligible_trainees?.length >
                    0 && selectedTraineeIds.length > 0 && (
                      <span className="text-deep-blue small font-weight-bold ml-3">
                        {selectedTraineeIds.length} not eligible trainee
                        {selectedTraineeIds.length > 1 ? "s" : ""} selected
                      </span>
                    )}
                </div>
              </CardHeader>
              <CardBody>

                {/* Filters */}
                <div className="d-flex align-items-center mb-3 flex-wrap flex-column flex-md-row w-100">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />
                    <Input
                      type="text"
                      placeholder="Search by email"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchEmail}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchEmail: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />
                    <Input
                      type="text"
                      placeholder="Search by mobile"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchMobile}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchMobile: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""
                        }`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Listing */}
                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation"></i>
                    </div>

                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords ? (
                  <div className="loaderContainer-xs empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>

                    <span>No records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={eligibleTableData || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={eligiblePageNum}
                      setPageNum={setEligiblePageNum}
                      totalPages={Math.ceil(
                        eligibleTraineesResponse?.data?.totalFilteredEligibleTraineesRecords /
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>

            <Card className="section-cards mt-3">
              <CardHeader className="section-header td-card-header">
                <div className="d-flex justify-content-between align-items-center flex-wrap w-100">
                  <h4 className="mb-0 text-default section-title">
                    Not Eligible Trainees
                    <span className="total-records ml-2">
                      (Total Records: {eligibleTraineesResponse?.data
                        ?.totalNotEligibleTraineesRecords || 0}
                    </span>)
                  </h4>

                  {eligibleTraineesResponse?.data?.eligible_trainees?.length === 0 && eligibleTraineesResponse?.data
                    ?.totalNotEligibleTraineesRecords > 0
                    && (
                      <Button
                        color="default"
                        onClick={handleIssueCertificate}
                        className="btn-deep-blue"
                      >
                        Issue Certificate
                      </Button>
                    )}
                </div>
                <div className="d-flex align-items-center flex-wrap w-100">
                  {eligibleTraineesResponse?.data?.eligible_trainees?.length === 0 &&
                    eligibleTraineesResponse?.data?.totalNotEligibleTraineesRecords > 0 && <div className="text-muted small">
                      <i className="fas fa-info-circle mr-1" />
                      To issue certificates to not eligible trainees, select the required
                      trainees using the checkboxes in this section.
                    </div>}

                  {eligibleTraineesResponse?.data?.eligible_trainees?.length === 0 && selectedTraineeIds.length > 0 && (
                    <span className="text-deep-blue small font-weight-bold ml-3">
                      {selectedTraineeIds.length} not eligible trainee
                      {selectedTraineeIds.length > 1 ? "s" : ""} selected
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : !eligibleTraineesResponse?.data?.not_eligible_trainees ||
                  eligibleTraineesResponse?.data?.not_eligible_trainees
                    ?.length === 0 ? (
                  <div className="loaderContainer-xs empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>

                    <span>No records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={notEligibleColumns}
                      data={notEligibleTableData || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={notEligiblePageNum}
                      setPageNum={setNotEligiblePageNum}
                      totalPages={Math.ceil(
                        eligibleTraineesResponse?.data
                          ?.totalNotEligibleTraineesRecords /
                        (eligibleTraineesResponse?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default IssuanceDetails;
